import {
  Injectable,
  OnInit,
  OnDestroy,
  Type,
  Injector,
  ComponentFactoryResolver,
} from '@angular/core';
import {
  Unboxed,
  SetValueAction,
  FormGroupState,
  MarkAsSubmittedAction,
  ResetAction,
} from 'ngrx-forms';
import {
  Store,
  Action,
} from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { DxFormGroup } from './form-group';
import { IDxForm } from './form';
import { FormEffect } from './decorators/form-effect';
import {
  Observable,
  Subscription,
  merge,
  from,
  Subject,
} from 'rxjs';
import {
  Actions,
  ofType,
} from '@ngrx/effects';
import {
  filter,
  mergeMap,
  groupBy,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  first,
  map,
  tap,
} from 'rxjs/operators';
import {
  RegisterForm,
  FormsActionTypes,
  SubmitValid,
  SubmitInvalid,
  SubmitSuccessful,
  SubmitError,
  DxSetValueAction,
} from './forms.actions';
import { getFormState } from './forms.selectors';
import { MOCK_FORM_STATE } from './tokens';
import {
  FORM_DEFINITION_METADATA_KEY,
  ON_SUBMIT_VALID_METADATA_KEY,
  ON_SUBMIT_INVALID_METADATA_KEY,
  ON_SUBMIT_METADATA_KEY,
  ON_FORM_ERROR_METADATA_KEY,
  ON_VALIDATION_ERROR_METADATA_KEY,
  ON_SUBMIT_SUCCESSFUL_METADATA_KEY,
  ON_SUBMIT_FAILED_METADATA_KEY,
} from './decorators/metadata-keys';
import { DxFormOnDefinition } from './decorators/interfaces';
import {
  deepEqual,
  getMetadata,
} from '@digitaix/helpers';
import { DocumentService } from '../documents/document.service';
import { LoadDocuments } from '../documents/document.actions';

export interface DxFormDefinition<FormValue> {
  formId: string | null;
  collectionPath: string | null;
  editComponent: any | null;
  title: string | null;
  load: string[];
}

export type HandlerFunction = () => any;

export interface OnSetValueHandler {
  handler: HandlerFunction;
  controlPaths: string[];
}

export function isAction(action: any): action is Action {
  return (
    typeof action !== 'function' &&
    action &&
    action.type &&
    typeof action.type === 'string'
  );
}

export function handelOnFunctionResults(results: any[]): Observable<Action> {
  const actions: Action[] = [];
  const asyncActions: Array<Observable<Action>> = [];

  for (const result of results) {
    if (result instanceof Observable) {
      asyncActions.push(result);
    } else if (Array.isArray(result)) {
      actions.push(...result);
    } else if (isAction(result)) {
      actions.push(result);
    }
  }

  return merge(...asyncActions, from(actions));
}

@Injectable()
export abstract class AbstractFormDefinition<
  FormValue,
  FormGroup extends DxFormGroup<FormValue, any, any> = DxFormGroup<
    FormValue,
    null,
    null
  >,
  Value = Unboxed<FormValue>
> implements DxFormDefinition<FormValue>, OnInit, OnDestroy {
  public formId: string | null = null;
  public collectionPath: string | null = null;
  public editComponent: any | null = null;
  public title: string | null;
  public load: string[] = [];
  public formState$!: Observable<FormGroupState<FormValue>>;
  public group!: FormGroup;

  /**
   * true - the document will only be added to the store
   * false - default behavior
   */
  public onlyLocal: boolean = false;

  public readonly actions$ = this._actions$.pipe(filter(() => this._active));

  public formActions$: Observable<
    Action & { formId: string }
  > = this.actions$.pipe(
    filter((action: any) => action.hasOwnProperty('formId')),
    filter(
      (action: { formId: string } & Action) => action.formId === this.formId
    ),
    groupBy(action => action.type),
    mergeMap(group => group.pipe(debounceTime(150)))
  );

  public controlActions$: Observable<
    Action & { controlId: string }
  > = this.actions$.pipe(
    filter((action: any) => action.hasOwnProperty('controlId')),
    filter(
      (action: { controlId: string } & Action) =>
        !!action.controlId.match(new RegExp(`^${this.formId}(\.|$)`))
    )
  );

  public setValueActions$: Observable<
    SetValueAction<any>
  > = this.controlActions$.pipe(
    ofType<SetValueAction<any>>(SetValueAction.TYPE),
    groupBy(action => action.controlId),
    mergeMap(group =>
      group.pipe(
        distinctUntilChanged(deepEqual),
        debounceTime(10),
      )
    ),
  );

  @FormEffect()
  public asyncValidators$: Observable<any> = this.setValueActions$.pipe(
    mergeMap(action => this.group.processAsyncValidators(action)),
    filter(isAction)
  );

  @FormEffect()
  public onSetValue$: Observable<any> = this.setValueActions$.pipe(
    tap(action => console.warn('On Set Value', action)),
    mergeMap(action =>
      this.group.processOnSetValue(action).pipe(filter(Boolean))
    ),
    filter(isAction)
  );

  @FormEffect()
  public onSubmitValid$: Observable<any> = this.formActions$.pipe(
    ofType<SubmitValid>(FormsActionTypes.SubmitValid),
    mergeMap(action =>
      handelOnFunctionResults(
        this._onSubmitValid.map(on => this[on](action.value)).filter(Boolean)
      )
    ),
    filter(isAction)
  );

  @FormEffect({ dispatch: false })
  public onReset$: Observable<any> = this.controlActions$.pipe(
    ofType<ResetAction>(ResetAction.TYPE),
    filter(action => action.controlId === this.formId),
    tap(() => this.__onReset$.next())
  );

  @FormEffect()
  public onSubmitInvalid$: Observable<any> = this.formActions$.pipe(
    ofType<SubmitInvalid>(FormsActionTypes.SubmitInvalid),
    mergeMap(action =>
      handelOnFunctionResults(
        this._onSubmitInvalid
          .map(on => this[on](action.value, action.errors))
          .filter(Boolean)
      )
    ),
    filter(isAction)
  );

  @FormEffect()
  public onSubmitSuccessful$: Observable<any> = this.formActions$.pipe(
    ofType<SubmitSuccessful>(FormsActionTypes.SubmitSuccessful),
    mergeMap(action =>
      handelOnFunctionResults(
        this._onSubmitSuccessful
          .map(on => this[on](action.result))
          .filter(Boolean)
      )
    ),
    filter(isAction)
  );

  @FormEffect()
  public onSubmitFailed$: Observable<any> = this.formActions$.pipe(
    ofType<SubmitError>(FormsActionTypes.SubmitError),
    mergeMap(action =>
      handelOnFunctionResults(
        this._onSubmitFailed.map(on => this[on](action.error)).filter(Boolean)
      )
    ),
    filter(isAction)
  );

  @FormEffect()
  public onSubmit$: Observable<any> = this.controlActions$.pipe(
    ofType<MarkAsSubmittedAction>(MarkAsSubmittedAction.TYPE),
    mergeMap(action =>
      merge(
        this.group.__state$.pipe(
          first(),
          filter(state => state.isValid),
          map(
            state =>
              new SubmitValid(
                this.formId,
                this.result,
                this.collectionPath ||
                  state.userDefinedProperties.collectionPath
              )
          )
        ),
        handelOnFunctionResults(
          this._onSubmit.map(on => this[on]()).filter(Boolean)
        )
      )
    ),
    filter(isAction)
  );

  public get initial(): FormValue {
    return this.group.__initial;
  }

  public get result(): Partial<Unboxed<FormValue>> {
    return this.group.__result;
  }

  private _onSubmitValid: string[] = [];
  private _onSubmitInvalid: string[] = [];
  private _onSubmit: string[] = [];
  private _onSubmitSuccessful: string[] = [];
  private _onSubmitFailed: string[] = [];
  private _onFormError: string[] = [];
  private _onValidationError: string[] = [];
  private _active = false;
  protected _subscriptions = new Subscription();
  public __onDestroy$ = new Subject<void>();
  public __onInit$ = new Subject<void>();
  public __onReset$ = new Subject<void>();

  constructor(
    public readonly store$: Store<any>,
    protected readonly _actions$: Actions,
    public readonly logger: NGXLogger,
    public readonly ds: DocumentService<any, any>,
    public readonly componentFactoryResolver: ComponentFactoryResolver,
    public readonly injector: Injector
  ) {
    const group: Type<FormGroup> = getMetadata(
      FORM_DEFINITION_METADATA_KEY,
      this.constructor
    );
    if (!group) {
      throw new Error(
        '[AbstractFormDefinition::constructor] main group constructor not found'
      );
    }
    this.group = new group(store$, logger, injector);
    if (!this.group) {
      throw new Error(
        '[AbstractFormDefinition::constructor] main group constructor could not be resolved in injector tree'
      );
    }
  }

  public box(value: Partial<Value>): FormValue {
    return {
      ...this.initial,
      ...this.group.box(value as any)
    };
  }

  public processesDecorates(): void {
    this.processOnFormError();
    this.processOnSubmit();
    this.processOnSubmitInvalid();
    this.processOnSubmitValid();
    this.processOnValidationError();
    this.processOnSubmitFailed();
    this.processOnSubmitSuccessful();
  }

  public processOnSubmitValid(): void {
    this._onSubmitValid = (
      getMetadata<DxFormOnDefinition[]>(
        ON_SUBMIT_VALID_METADATA_KEY,
        Object.getPrototypeOf(this)
      ) || []
    )
      .sort((a, b) => a.order - b.order)
      .map(on => on.propertyKey);
  }

  public processOnSubmitInvalid(): void {
    this._onSubmitInvalid = (
      getMetadata<DxFormOnDefinition[]>(
        ON_SUBMIT_INVALID_METADATA_KEY,
        Object.getPrototypeOf(this)
      ) || []
    )
      .sort((a, b) => a.order - b.order)
      .map(on => on.propertyKey);
  }

  public processOnSubmit(): void {
    this._onSubmit = (
      getMetadata<DxFormOnDefinition[]>(
        ON_SUBMIT_METADATA_KEY,
        Object.getPrototypeOf(this)
      ) || []
    )
      .sort((a, b) => a.order - b.order)
      .map(on => on.propertyKey);
  }

  public processOnSubmitSuccessful(): void {
    this._onSubmitSuccessful = (
      getMetadata<DxFormOnDefinition[]>(
        ON_SUBMIT_SUCCESSFUL_METADATA_KEY,
        Object.getPrototypeOf(this)
      ) || []
    )
      .sort((a, b) => a.order - b.order)
      .map(on => on.propertyKey);
  }

  public processOnSubmitFailed(): void {
    this._onSubmitFailed = (
      getMetadata<DxFormOnDefinition[]>(
        ON_SUBMIT_FAILED_METADATA_KEY,
        Object.getPrototypeOf(this)
      ) || []
    )
      .sort((a, b) => a.order - b.order)
      .map(on => on.propertyKey);
  }

  public processOnFormError(): void {
    this._onFormError = (
      getMetadata<DxFormOnDefinition[]>(
        ON_FORM_ERROR_METADATA_KEY,
        Object.getPrototypeOf(this)
      ) || []
    )
      .sort((a, b) => a.order - b.order)
      .map(on => on.propertyKey);
  }

  public processOnValidationError(): void {
    this._onValidationError = (
      getMetadata<DxFormOnDefinition[]>(
        ON_VALIDATION_ERROR_METADATA_KEY,
        Object.getPrototypeOf(this)
      ) || []
    )
      .sort((a, b) => a.order - b.order)
      .map(on => on.propertyKey);
  }

  /**
   * @internal
   */
  public init(): void {
    if (this.title === undefined) {
      this.title = ['FORMS', this.formId, 'TITLE'].join('.');
    }
    this.group.__definition = this as any;
    this.processesDecorates();
    this.group.setControlId(this.formId);
    this.group.init();
    this.logger.debug(
      `[AbstractFormDefinition::init::${this.formId}] RegisterForm`
    );
    this.store$.dispatch(
      new RegisterForm(this.formId, {
        initial:               this.initial,
        collectionPath:        this.collectionPath,
        load:                  this.load,
        title:                 this.title,
        box:                   this.box.bind(this),
        validateAndUpdateForm: this.group.__validateAndUpdateGroup,
        editComponent:         this.editComponent,
        withDefinition:        true,
        onlyLocal:             this.onlyLocal,
      })
    );
    // TODO : check race condition issue
    // erste name dem erfolgreichen Register des Froms darf
    // in DxFormControl der ngAfterInit hook ausgefÃ¼hrt werden
    this.group.__initialized = true;
  }

  public ngOnInit(): void {
    this._active = true;
    this.logger.trace(
      `[AbstractFormDefinition::ngOnInit::${this.formId}] call`
    );

    const formState: FormGroupState<FormValue> | null = this.injector.get(
      MOCK_FORM_STATE,
      null
    );
    if (formState) {
      this.store$.dispatch(new DxSetValueAction(this.formId, formState.value, `AbstractFormDefinition(${this.constructor.name})::ngOnInit`));
      this.logger.info(
        `[AbstractFormDefinition::constructor::${
          formState.id
        }] use mock form state`,
        formState
      );
    }
    this.formState$ = this.store$
      .select(getFormState(this.formId))
      .pipe(
        distinctUntilChanged((a, b) => deepEqual(a, b)),
        takeUntil<FormGroupState<FormValue>>(this.__onDestroy$)
      );
    this.load.forEach(load => this.store$.dispatch(new LoadDocuments(load)));
    this.group.__definition = this as any;
    // run before definition init to build the initial value
    // TODO : fix typing
    this.group.ngOnInit();
    this._subscriptions.add(
      this.__onReset$.subscribe(() => this.group.__onReset$.next())
    );
    this.__onInit$.next();
  }

  public ngOnDestroy(): void {
    this._active = false;
    this._subscriptions.unsubscribe();
    this._subscriptions = new Subscription();
    this.logger.trace(
      `[AbstractFormDefinition::ngOnDestroy::${this.formId}] call`
    );
    this.group.ngOnDestroy();
    this.__onDestroy$.next();
  }

  public getValue<V>(controlPath: string): V {
    return this.group.getValue(controlPath);
  }

  public getValue$<V>(controlPath: string): Observable<V> {
    return this.group.getValue$(controlPath);
  }

  public getControl<V, D extends IDxForm<V, any, any>>(controlPath: string): D {
    return this.group.getControl(controlPath);
  }
}
